import { Component, EventEmitter, OnInit, Input, ViewChild, ElementRef, Output } from '@angular/core';
import * as d3 from 'd3';
import invertedStep from 'src/app/shared/d3/invertedStep';

const equal = require('deep-equal');
const NODE_WIDTH = 170;
const NODE_HEIGHT = 85;
const PADDING = 24;

@Component({
  selector: 'org-chart',
  templateUrl: './org-chart.component.html',
  styleUrls: ['./org-chart.component.scss']
})
export class OrgChartComponent implements OnInit {
  private _nodes;
  @Input() nodes;
  @Output() onClicked = new EventEmitter();
  @ViewChild('svg') svgRef: ElementRef;
  svg;
  tree;
  zoom;
    
  constructor() { }

  ngOnInit() {
  }
  
  ngAfterViewChecked() {
    if (!equal(this.nodes, this._nodes) &&
      this.svgRef.nativeElement.clientWidth > 0) {
      this._nodes = this.nodes;
      this.initialize();
    } else if (this._nodes) {
      this.refreshChart();
    }
  }

  /**
   * Initializes the org chart SVG.
   */
  initialize() {
    const svg = this.svgRef.nativeElement;
    this.svg = d3.select(svg);
    this.svg.selectAll('.main *').remove();
    const hierarchy = d3.hierarchy(this.nodes[0]);
    this.tree = d3.tree()
      .nodeSize([200, 200])
      .separation((nodeA, nodeB) => nodeA.parent === nodeB.parent ? 1 : 1)(hierarchy);
    const main = this.svg.select('.main');
    const linkPath = d3.line().x((node: any) => node.x).y((node: any) => node.y)
      .curve(invertedStep);

    const link = main.selectAll('.link')
      .data(this.tree.descendants().slice(1))
      .enter()
      .append('g')
      .attr('class', 'link')
      .attr('transform', () => {
        return `translate(${NODE_WIDTH / 2}, ${NODE_HEIGHT / 2})`;
      });
    // TODO: Color constants.
    link.append('path')
      .style('fill', 'none')
      .attr('stroke', '#655DC6')
      .attr('stroke-width', '1')
      .attr('d', (node) => {
        return linkPath([node, node.parent]);
      });

    const onClicked = this.onClicked;
    const nodegroup = main.selectAll('.nodegroup')
      .data(this.tree.descendants())
      .enter()
      .append('g')
      .attr('class', 'nodegroup')
      .attr('transform', (node) => {
        const xCoord = node.x;
        const yCoord = node.y;
        return `translate(${xCoord}, ${yCoord})`;
      })
      // TODO: Color constants.
      .on('mouseover', function () {
        d3.select(this).select('rect')
          .style('fill', '#E0DFF4');
        d3.select(this).selectAll('*')
          .style('cursor', 'pointer');
      })
      // TODO: Color constants.
      .on('mouseout', function () {
        d3.select(this).select('rect')
          .style('fill', '#EFEEF9');
        d3.select(this).selectAll('*')
          .style('cursor', 'default');
      })
      .on('click', function () {
        onClicked.emit((d3.select(this).datum() as any).data.entity.entityId);
      });

    // TODO: Color constants.
    nodegroup.append('rect')
      .attr('class', 'node')
      .attr('rx', '4')
      .attr('ry', '4')
      .attr('stroke', '#C1BEE8')
      .attr('stroke-width', '1')
      .style('fill', '#EFEEF9')
      .attr('width', NODE_WIDTH.toString())
      .attr('height', NODE_HEIGHT.toString());
    // TODO: Color constants.
    nodegroup.append('text')
      .style('fill', '#655DC6')
      .style('font-size', '15')
      .style('font-weight', '500')
      .attr('text-anchor', 'middle')
      .attr('x', (NODE_WIDTH / 2).toString())
      .attr('y', (NODE_HEIGHT / 2 - 5).toString())
      .text(({ data }) => {
        return data.name;
      });
    nodegroup.append('text')
      .style('fill', '#655DC6')
      .attr('class', 'ownership')
      .style('font-size', '15')
      .style('font-weight', '500')
      .attr('y', '-18')
      .text(({ data }) => {
        if (!data.ownership) {
          return '';
        }
        let ownership = Number(data.ownership * 100);
        if (ownership > 0) {
          return `${ownership}%`;
        }
        return `${ownership.toFixed(2)}%`;
      })
      .attr('x', function () {
        return (NODE_WIDTH - this.getBBox().width).toString();
      });
    
    // TODO: Color and numerical constants.
    nodegroup.append('text')
      .style('fill', '#212121')
      .style('font-size', '14')
      .attr('text-anchor', 'middle')
      .attr('x', (NODE_WIDTH / 2).toString())
      .attr('y', (NODE_HEIGHT / 2 + 15).toString())
      .text(({ data }) => {
        return data.entity.domicileCode;
      });
          
    // HACK: This code sucks.
    setTimeout(() => this.refreshChart(), 100);
    setTimeout(() => this.refreshChart(), 500);
    setTimeout(() => this.refreshChart(), 2000);
    setTimeout(() => this.refreshChart(), 3500);
    setTimeout(() => this.initializeZoom(), 100);
  }

  /**
   * Initializes the zoom functionality for the org chart.
   */
  initializeZoom() {
    let minX = null;
    let maxX = null;
    let minY = null;
    let maxY = null;
    this.tree.descendants().forEach((item) => {
      if (!minX || item.x < minX) {
        minX = item.x;
      }
      if (!maxX || item.x > maxX) {
        maxX = item.x;
      }
      if (!minY || item.y < minY) {
        minY = item.y;
      }
      if (!maxY || item.y > maxY) {
        maxY = item.y;
      }
    });
    const width = maxX - minX;
    const height = maxY - minY;

    const elWidth = this.svgRef.nativeElement.clientWidth;

    const svg = this.svg;
    // TODO: Numeical constants.
    const zoom = this.zoom = d3.zoom()
      .extent([[0 - width / 2, 0], [width / 2, height / 2]])
      .scaleExtent([0.2, 1.25])
      .on('zoom', function () {
        const { transform } = d3.event;
        svg.select('.main')
          .attr('transform', transform);
      });
    this.svg
      .call(zoom)
      .call(zoom.transform, d3.zoomIdentity
        .translate(this.tree.descendants()[0].x + elWidth / 2 - PADDING * 2, 0).scale(0.5));
  }

  /**
   * Translates the nodes on this chart.
   */
  refreshChart() {
    this.svg.selectAll('.nodegroup')
      .attr('transform', (node) => {
        const xCoord = node.x;
        const yCoord = node.y;
        return `translate(${xCoord}, ${yCoord})`;
      });

    this.svg.selectAll('.ownership')
      .attr('x', function () {
        return (NODE_WIDTH - this.getBBox().width).toString();
      });
  }

  /**
   * Zooms in.
   */
  zoomIn() {
    const delta = (this.zoom.scaleExtent()[1] - this.zoom.scaleExtent()[0]) /
      100;
    this.zoom.scaleTo(this.svg, d3.zoomTransform(this.svgRef.nativeElement).k + delta);
  }

  /**
   * Zooms out.
   */
  zoomOut() {
    const delta = (this.zoom.scaleExtent()[1] - this.zoom.scaleExtent()[0]) /
      100;
    this.zoom.scaleTo(this.svg, d3.zoomTransform(this.svgRef.nativeElement).k - delta);
  }
}
